/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Button,
    TextField,
    Grid,
    Paper,
    Chip,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Slider,
    Divider,
    IconButton,
    Collapse,
    Skeleton,
    useMediaQuery,
    useTheme,
    Drawer,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Breadcrumbs,
    Link,
    Rating,
    Popover,
} from '@mui/material';
import {
    Attractions,
    LocationOn,
    CalendarToday,
    Person,
    Search,
    FilterList,
    ExpandMore,
    ExpandLess,
    CheckCircle,
    Close,
    TuneRounded,
    Home,
    AccessTime,
    Star,
    Favorite,
    FavoriteBorder,
    LocalOffer,
    Groups,
    Hiking,
    Museum,
    Restaurant,
    Sailing,
    SportsEsports,
    Nightlife,
    Park,
    TheaterComedy,
    Add,
    Remove,
    ChildCare,
} from '@mui/icons-material';

// ============================================================================
// TYPES
// ============================================================================

interface ActivityResult {
    id: string;
    name: string;
    description: string;
    category: string;
    image: string;
    images: string[];
    location: string;
    duration: string;
    durationHours: number;
    price: number;
    originalPrice?: number;
    currency: string;
    rating: number;
    reviewCount: number;
    highlights: string[];
    includes: string[];
    freeCancellation: boolean;
    instantConfirmation: boolean;
    mobileTicket: boolean;
    maxGroupSize?: number;
    languages: string[];
    bestSeller?: boolean;
    likelyToSellOut?: boolean;
}

interface SearchFilters {
    priceRange: [number, number];
    categories: string[];
    duration: string[];
    rating: number;
    freeCancellation: boolean;
}

type SortOption = 'recommended' | 'price_low' | 'price_high' | 'rating' | 'most_popular';

// ============================================================================
// CONSTANTS
// ============================================================================

const ACTIVITY_CATEGORIES = [
    { key: 'tours', label: 'Tours & Sightseeing', icon: <Attractions /> },
    { key: 'outdoor', label: 'Outdoor Activities', icon: <Hiking /> },
    { key: 'museums', label: 'Museums & Attractions', icon: <Museum /> },
    { key: 'food', label: 'Food & Drink', icon: <Restaurant /> },
    { key: 'water', label: 'Water Activities', icon: <Sailing /> },
    { key: 'entertainment', label: 'Entertainment', icon: <SportsEsports /> },
    { key: 'nightlife', label: 'Nightlife', icon: <Nightlife /> },
    { key: 'nature', label: 'Nature & Parks', icon: <Park /> },
    { key: 'shows', label: 'Shows & Events', icon: <TheaterComedy /> },
];

const DURATION_OPTIONS = [
    { key: 'short', label: 'Up to 1 hour', maxHours: 1 },
    { key: 'half', label: '1-4 hours', maxHours: 4 },
    { key: 'full', label: '4-8 hours', maxHours: 8 },
    { key: 'multi', label: 'Full day (8+ hours)', maxHours: 24 },
];

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'recommended', label: 'Recommended' },
    { value: 'most_popular', label: 'Most Popular' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' },
];

// ============================================================================
// MOCK DATA GENERATOR
// ============================================================================

const generateMockActivities = (
    destination: string,
    date: string,
    adults: number,
    children: number,
    keyword: string
): ActivityResult[] => {
    const activities: ActivityResult[] = [
        {
            id: 'activity-1',
            name: `${destination} Highlights Walking Tour`,
            description: `Discover the best of ${destination} on this guided walking tour. Visit iconic landmarks, hidden gems, and learn about the city's rich history and culture.`,
            category: 'tours',
            image: 'https://images.unsplash.com/photo-1569949381669-ecf31ae8c37e?w=400',
            images: [],
            location: `Downtown ${destination}`,
            duration: '3 hours',
            durationHours: 3,
            price: 45,
            currency: 'USD',
            rating: 4.8,
            reviewCount: 1234,
            highlights: ['Small group experience', 'Expert local guide', 'Hidden gems'],
            includes: ['Professional guide', 'Small group tour', 'Hotel pickup'],
            freeCancellation: true,
            instantConfirmation: true,
            mobileTicket: true,
            maxGroupSize: 15,
            languages: ['English', 'Spanish', 'French'],
            bestSeller: true,
        },
        {
            id: 'activity-2',
            name: `Skip-the-Line ${destination} Museum Pass`,
            description: `Get priority access to the top museums in ${destination}. Save time and avoid long queues with this convenient pass.`,
            category: 'museums',
            image: 'https://images.unsplash.com/photo-1554907984-15263bfd63bd?w=400',
            images: [],
            location: destination,
            duration: 'Full day',
            durationHours: 8,
            price: 65,
            originalPrice: 85,
            currency: 'USD',
            rating: 4.6,
            reviewCount: 856,
            highlights: ['Skip-the-line access', 'Multiple museums', 'Valid for 48 hours'],
            includes: ['Entry to 5 museums', 'Skip-the-line access', 'Audio guide'],
            freeCancellation: true,
            instantConfirmation: true,
            mobileTicket: true,
            languages: ['English'],
            likelyToSellOut: true,
        },
        {
            id: 'activity-3',
            name: `${destination} Food & Market Tour`,
            description: `Taste your way through ${destination}'s culinary scene. Sample local delicacies, visit bustling markets, and learn about regional cuisine.`,
            category: 'food',
            image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400',
            images: [],
            location: `Market District, ${destination}`,
            duration: '4 hours',
            durationHours: 4,
            price: 79,
            currency: 'USD',
            rating: 4.9,
            reviewCount: 567,
            highlights: ['10+ tastings included', 'Local markets', 'Small group'],
            includes: ['Food tastings', 'Local guide', 'Market visits', 'Recipes'],
            freeCancellation: true,
            instantConfirmation: true,
            mobileTicket: true,
            maxGroupSize: 12,
            languages: ['English', 'Spanish'],
            bestSeller: true,
        },
        {
            id: 'activity-4',
            name: `${destination} Sunset Sailing Cruise`,
            description: `Experience a magical sunset on the water with this relaxing sailing cruise. Enjoy drinks and snacks while taking in stunning views.`,
            category: 'water',
            image: 'https://images.unsplash.com/photo-1500514966906-fe245eea9344?w=400',
            images: [],
            location: `${destination} Harbor`,
            duration: '2 hours',
            durationHours: 2,
            price: 89,
            currency: 'USD',
            rating: 4.7,
            reviewCount: 423,
            highlights: ['Sunset views', 'Drinks included', 'Professional crew'],
            includes: ['Sailing cruise', 'Wine & snacks', 'Professional captain'],
            freeCancellation: true,
            instantConfirmation: true,
            mobileTicket: true,
            maxGroupSize: 20,
            languages: ['English'],
            likelyToSellOut: true,
        },
        {
            id: 'activity-5',
            name: `${destination} Bike Tour`,
            description: `Explore ${destination} on two wheels! This fun bike tour covers more ground than walking and includes stops at major attractions.`,
            category: 'outdoor',
            image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400',
            images: [],
            location: destination,
            duration: '3.5 hours',
            durationHours: 3.5,
            price: 55,
            currency: 'USD',
            rating: 4.8,
            reviewCount: 789,
            highlights: ['Bike & helmet included', 'Flat route', 'Photo stops'],
            includes: ['Bike rental', 'Helmet', 'Guide', 'Snacks'],
            freeCancellation: true,
            instantConfirmation: true,
            mobileTicket: true,
            maxGroupSize: 15,
            languages: ['English', 'German'],
        },
        {
            id: 'activity-6',
            name: `${destination} Nightlife Pub Crawl`,
            description: `Experience the best nightlife in ${destination}! Visit top bars and clubs with skip-the-line entry and drink specials.`,
            category: 'nightlife',
            image: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400',
            images: [],
            location: `Entertainment District, ${destination}`,
            duration: '4 hours',
            durationHours: 4,
            price: 35,
            currency: 'USD',
            rating: 4.5,
            reviewCount: 345,
            highlights: ['4 venue visits', 'Skip-the-line', 'Drink specials'],
            includes: ['Guide', 'Entry to 4 venues', '1 free drink per venue'],
            freeCancellation: true,
            instantConfirmation: true,
            mobileTicket: true,
            maxGroupSize: 25,
            languages: ['English'],
        },
        {
            id: 'activity-7',
            name: `${destination} Day Trip to Countryside`,
            description: `Escape the city and explore the beautiful countryside around ${destination}. Visit charming villages, wineries, and scenic viewpoints.`,
            category: 'tours',
            image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400',
            images: [],
            location: `${destination} region`,
            duration: '10 hours',
            durationHours: 10,
            price: 125,
            originalPrice: 150,
            currency: 'USD',
            rating: 4.9,
            reviewCount: 234,
            highlights: ['Small group', 'Wine tasting', 'Lunch included'],
            includes: ['Transport', 'Guide', 'Lunch', 'Wine tasting'],
            freeCancellation: true,
            instantConfirmation: true,
            mobileTicket: true,
            maxGroupSize: 8,
            languages: ['English', 'French'],
        },
        {
            id: 'activity-8',
            name: `${destination} Comedy Show Tickets`,
            description: `Laugh the night away at ${destination}'s premier comedy club. See top comedians in an intimate venue.`,
            category: 'shows',
            image: 'https://images.unsplash.com/photo-1527224538127-2104bb71c51b?w=400',
            images: [],
            location: `Comedy Club, ${destination}`,
            duration: '2 hours',
            durationHours: 2,
            price: 25,
            currency: 'USD',
            rating: 4.4,
            reviewCount: 189,
            highlights: ['Top comedians', 'Intimate venue', 'Drinks available'],
            includes: ['Show ticket', 'Reserved seating'],
            freeCancellation: false,
            instantConfirmation: true,
            mobileTicket: true,
            languages: ['English'],
        },
        {
            id: 'activity-9',
            name: `${destination} National Park Hike`,
            description: `Discover the natural beauty around ${destination} with this guided hiking experience. Suitable for all fitness levels.`,
            category: 'nature',
            image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=400',
            images: [],
            location: `National Park near ${destination}`,
            duration: '6 hours',
            durationHours: 6,
            price: 75,
            currency: 'USD',
            rating: 4.8,
            reviewCount: 456,
            highlights: ['Scenic trails', 'Expert naturalist guide', 'Transport included'],
            includes: ['Guide', 'Transport', 'Snacks', 'Water'],
            freeCancellation: true,
            instantConfirmation: true,
            mobileTicket: true,
            maxGroupSize: 12,
            languages: ['English', 'Spanish'],
        },
        {
            id: 'activity-10',
            name: `${destination} Cooking Class`,
            description: `Learn to cook authentic local dishes in this hands-on cooking class. Includes market visit and full meal.`,
            category: 'food',
            image: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400',
            images: [],
            location: `Culinary School, ${destination}`,
            duration: '4 hours',
            durationHours: 4,
            price: 95,
            currency: 'USD',
            rating: 4.9,
            reviewCount: 312,
            highlights: ['Hands-on cooking', 'Market visit', 'Recipe booklet'],
            includes: ['Ingredients', 'Apron', 'Recipe booklet', 'Full meal', 'Wine'],
            freeCancellation: true,
            instantConfirmation: true,
            mobileTicket: true,
            maxGroupSize: 10,
            languages: ['English'],
            bestSeller: true,
        },
    ];

    // Filter by keyword if provided
    if (keyword) {
        const kw = keyword.toLowerCase();
        return activities.filter(a => 
            a.name.toLowerCase().includes(kw) || 
            a.description.toLowerCase().includes(kw) ||
            a.category.toLowerCase().includes(kw)
        );
    }

    return activities;
};

// ============================================================================
// STEPPER INPUT COMPONENT
// ============================================================================

interface StepperInputProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    label: string;
    sublabel?: string;
    icon?: React.ReactNode;
}

const StepperInput: React.FC<StepperInputProps> = ({ value, onChange, min = 0, max = 10, label, sublabel, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
                {sublabel && <Typography variant="caption" color="text.secondary">{sublabel}</Typography>}
            </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => value > min && onChange(value - 1)} disabled={value <= min} sx={{ border: '1px solid', borderColor: value <= min ? '#e0e0e0' : '#2D6E6A', color: value <= min ? '#e0e0e0' : '#2D6E6A', width: 32, height: 32 }}>
                <Remove fontSize="small" />
            </IconButton>
            <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>{value}</Typography>
            <IconButton size="small" onClick={() => value < max && onChange(value + 1)} disabled={value >= max} sx={{ border: '1px solid', borderColor: value >= max ? '#e0e0e0' : '#2D6E6A', color: value >= max ? '#e0e0e0' : '#2D6E6A', width: 32, height: 32 }}>
                <Add fontSize="small" />
            </IconButton>
        </Box>
    </Box>
);

// ============================================================================
// ACTIVITY CARD COMPONENT
// ============================================================================

interface ActivityCardProps {
    activity: ActivityResult;
    travelers: number;
    onSelect: (activity: ActivityResult) => void;
}

const ActivityCard: React.FC<ActivityCardProps> = ({ activity, travelers, onSelect }) => {
    const [liked, setLiked] = useState(false);

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                transition: 'box-shadow 0.2s, border-color 0.2s',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderColor: '#2D6E6A' },
            }}
        >
            {/* Image */}
            <Box sx={{ position: 'relative', width: { xs: '100%', sm: 280 }, minWidth: { sm: 280 } }}>
                <CardMedia
                    component="img"
                    sx={{ height: { xs: 200, sm: '100%' }, minHeight: { sm: 240 }, objectFit: 'cover' }}
                    image={activity.image}
                    alt={activity.name}
                />
                <IconButton
                    onClick={(e) => { e.stopPropagation(); setLiked(!liked); }}
                    sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'white', '&:hover': { bgcolor: 'white' } }}
                >
                    {liked ? <Favorite sx={{ color: '#F44336' }} /> : <FavoriteBorder />}
                </IconButton>
                {activity.bestSeller && (
                    <Chip label="Best Seller" size="small" sx={{ position: 'absolute', top: 12, left: 12, bgcolor: '#FF6B35', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                )}
                {activity.likelyToSellOut && !activity.bestSeller && (
                    <Chip label="Likely to Sell Out" size="small" sx={{ position: 'absolute', top: 12, left: 12, bgcolor: '#F44336', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                )}
            </Box>

            {/* Content */}
            <CardContent sx={{ flex: 1, p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1, pr: 2 }}>
                        {/* Category */}
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {ACTIVITY_CATEGORIES.find(c => c.key === activity.category)?.label || activity.category}
                        </Typography>

                        {/* Name */}
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' }, lineHeight: 1.3, mb: 0.5 }}>
                            {activity.name}
                        </Typography>

                        {/* Location & Duration */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOn sx={{ fontSize: 16 }} /> {activity.location}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTime sx={{ fontSize: 16 }} /> {activity.duration}
                            </Typography>
                            {activity.maxGroupSize && (
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Groups sx={{ fontSize: 16 }} /> Max {activity.maxGroupSize}
                                </Typography>
                            )}
                        </Box>

                        {/* Description */}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {activity.description}
                        </Typography>

                        {/* Highlights */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {activity.highlights.slice(0, 3).map((highlight) => (
                                <Chip key={highlight} label={highlight} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#E8F5E9', color: '#2E7D32' }} />
                            ))}
                        </Box>

                        {/* Policies */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {activity.freeCancellation && (
                                <Typography variant="caption" sx={{ color: '#2E7D32', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CheckCircle sx={{ fontSize: 14 }} /> Free cancellation
                                </Typography>
                            )}
                            {activity.instantConfirmation && (
                                <Typography variant="caption" sx={{ color: '#1976D2' }}>
                                    Instant confirmation
                                </Typography>
                            )}
                            {activity.mobileTicket && (
                                <Typography variant="caption" color="text.secondary">
                                    Mobile ticket
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Price & Rating */}
                    <Box sx={{ textAlign: 'right', minWidth: { xs: 80, sm: 120 } }}>
                        {/* Rating */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mb: 1 }}>
                            <Rating value={activity.rating} precision={0.1} size="small" readOnly />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{activity.rating}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {activity.reviewCount.toLocaleString()} reviews
                        </Typography>

                        {/* Price */}
                        <Box sx={{ mt: 1 }}>
                            {activity.originalPrice && (
                                <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                    ${activity.originalPrice}
                                </Typography>
                            )}
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                ${activity.price}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">per person</Typography>
                            {travelers > 1 && (
                                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    ${activity.price * travelers} total
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Book button */}
                <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={() => onSelect(activity)}
                        sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4, '&:hover': { bgcolor: '#009688' } }}
                    >
                        Check Availability
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

// ============================================================================
// FILTER SIDEBAR COMPONENT
// ============================================================================

interface FilterSidebarProps {
    filters: SearchFilters;
    onChange: (filters: SearchFilters) => void;
    maxPrice: number;
    resultCount: number;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ filters, onChange, maxPrice, resultCount }) => {
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        price: true,
        category: true,
        duration: true,
        rating: true,
        policies: false,
    });

    const toggleSection = (section: string) => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const updateFilter = <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
        onChange({ ...filters, [key]: value });
    };

    const toggleArrayFilter = (key: 'categories' | 'duration', value: string) => {
        const current = filters[key];
        const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        updateFilter(key, updated);
    };

    const FilterSection: React.FC<{ title: string; id: string; children: React.ReactNode }> = ({ title, id, children }) => (
        <Box sx={{ mb: 2 }}>
            <Box onClick={() => toggleSection(id)} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', py: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
                {expandedSections[id] ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Collapse in={expandedSections[id]}><Box sx={{ pt: 1 }}>{children}</Box></Collapse>
            <Divider sx={{ mt: 2 }} />
        </Box>
    );

    return (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList /> Filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{resultCount} activities found</Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Price */}
            <FilterSection title="Price" id="price">
                <Slider
                    value={filters.priceRange}
                    onChange={(_, value) => updateFilter('priceRange', value as [number, number])}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `$${v}`}
                    min={0}
                    max={maxPrice}
                    sx={{ color: '#2D6E6A' }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption">${filters.priceRange[0]}</Typography>
                    <Typography variant="caption">${filters.priceRange[1]}</Typography>
                </Box>
            </FilterSection>

            {/* Category */}
            <FilterSection title="Category" id="category">
                <FormGroup>
                    {ACTIVITY_CATEGORIES.map((cat) => (
                        <FormControlLabel
                            key={cat.key}
                            control={<Checkbox checked={filters.categories.includes(cat.key)} onChange={() => toggleArrayFilter('categories', cat.key)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{cat.icon}{cat.label}</Box>}
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Duration */}
            <FilterSection title="Duration" id="duration">
                <FormGroup>
                    {DURATION_OPTIONS.map((dur) => (
                        <FormControlLabel
                            key={dur.key}
                            control={<Checkbox checked={filters.duration.includes(dur.key)} onChange={() => toggleArrayFilter('duration', dur.key)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                            label={dur.label}
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Rating */}
            <FilterSection title="Rating" id="rating">
                <FormGroup>
                    {[4.5, 4, 3.5, 3].map((rating) => (
                        <FormControlLabel
                            key={rating}
                            control={<Checkbox checked={filters.rating === rating} onChange={() => updateFilter('rating', filters.rating === rating ? 0 : rating)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Rating value={rating} precision={0.5} size="small" readOnly /> & up</Box>}
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Policies */}
            <FilterSection title="Policies" id="policies">
                <FormControlLabel
                    control={<Checkbox checked={filters.freeCancellation} onChange={(e) => updateFilter('freeCancellation', e.target.checked)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                    label="Free cancellation"
                />
            </FilterSection>
        </Paper>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ActivitySearchResultsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Parse URL params
    const initialDestination = searchParams.get('destination') || '';
    const initialDate = searchParams.get('date') || '';
    const initialAdults = parseInt(searchParams.get('adults') || '2', 10);
    const initialChildren = parseInt(searchParams.get('children') || '0', 10);
    const initialKeyword = searchParams.get('keyword') || '';

    // Search state
    const [destination, setDestination] = useState(initialDestination);
    const [date, setDate] = useState(initialDate);
    const [adults, setAdults] = useState(initialAdults);
    const [children, setChildren] = useState(initialChildren);
    const [keyword, setKeyword] = useState(initialKeyword);

    // Results state
    const [loading, setLoading] = useState(true);
    const [activities, setActivities] = useState<ActivityResult[]>([]);

    // Filter state
    const [filters, setFilters] = useState<SearchFilters>({
        priceRange: [0, 200],
        categories: [],
        duration: [],
        rating: 0,
        freeCancellation: false,
    });

    // Sort state
    const [sortBy, setSortBy] = useState<SortOption>('recommended');

    // Mobile filter drawer
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // Total travelers
    const totalTravelers = adults + children;

    // Load results
    useEffect(() => {
        const loadResults = async () => {
            setLoading(true);
            await new Promise((resolve) => setTimeout(resolve, 1200));

            if (initialDestination) {
                const mockActivities = generateMockActivities(initialDestination, initialDate, initialAdults, initialChildren, initialKeyword);
                setActivities(mockActivities);
                const maxPrice = Math.max(...mockActivities.map((a) => a.price), 200);
                setFilters((prev) => ({ ...prev, priceRange: [0, maxPrice] }));
            }
            setLoading(false);
        };
        loadResults();
    }, [initialDestination, initialDate, initialAdults, initialChildren, initialKeyword]);

    // Filter and sort
    const filteredActivities = useMemo(() => {
        let result = [...activities];

        result = result.filter((activity) => {
            if (activity.price < filters.priceRange[0] || activity.price > filters.priceRange[1]) return false;
            if (filters.categories.length > 0 && !filters.categories.includes(activity.category)) return false;
            if (filters.duration.length > 0) {
                const matchesDuration = filters.duration.some((d) => {
                    const opt = DURATION_OPTIONS.find((o) => o.key === d);
                    if (!opt) return false;
                    if (d === 'short') return activity.durationHours <= 1;
                    if (d === 'half') return activity.durationHours > 1 && activity.durationHours <= 4;
                    if (d === 'full') return activity.durationHours > 4 && activity.durationHours <= 8;
                    if (d === 'multi') return activity.durationHours > 8;
                    return false;
                });
                if (!matchesDuration) return false;
            }
            if (filters.rating > 0 && activity.rating < filters.rating) return false;
            if (filters.freeCancellation && !activity.freeCancellation) return false;
            return true;
        });

        switch (sortBy) {
            case 'price_low': result.sort((a, b) => a.price - b.price); break;
            case 'price_high': result.sort((a, b) => b.price - a.price); break;
            case 'rating': result.sort((a, b) => b.rating - a.rating); break;
            case 'most_popular': result.sort((a, b) => b.reviewCount - a.reviewCount); break;
            default: result.sort((a, b) => (b.bestSeller ? 1 : 0) - (a.bestSeller ? 1 : 0) || b.rating - a.rating);
        }

        return result;
    }, [activities, filters, sortBy]);

    // Handle activity selection
    const handleSelectActivity = (activity: ActivityResult) => {
        const params = new URLSearchParams({
            type: 'activity',
            activityId: activity.id,
            activityName: activity.name,
            destination,
            date,
            travelers: totalTravelers.toString(),
            pricePerPerson: activity.price.toString(),
            totalPrice: (activity.price * totalTravelers).toString(),
        });
        navigate(`/book/checkout?${params.toString()}`);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F5F5F5' }}>
            {/* Header */}
            <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, zIndex: 100 }}>
                <Container maxWidth="xl">
                    <Box sx={{ py: 1 }}>
                        <Breadcrumbs sx={{ fontSize: '0.85rem' }}>
                            <Link component="button" onClick={() => navigate('/')} underline="hover" color="inherit" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Home sx={{ fontSize: 16 }} /> Home
                            </Link>
                            <Link component="button" onClick={() => navigate(-1)} underline="hover" color="inherit">Activities</Link>
                            <Typography color="text.primary" sx={{ fontSize: '0.85rem' }}>{destination}</Typography>
                        </Breadcrumbs>
                    </Box>

                    {/* Search Summary */}
                    <Box sx={{ py: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 2, border: '1px solid #e0e0e0', flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOn sx={{ color: '#2D6E6A' }} />
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{destination || 'Destination'}</Typography>
                                </Box>
                                <Divider orientation="vertical" flexItem />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarToday sx={{ fontSize: 18, color: '#666' }} />
                                    <Typography variant="body2">
                                        {date ? new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'Any date'}
                                    </Typography>
                                </Box>
                                <Divider orientation="vertical" flexItem />
                                <Typography variant="body2">{totalTravelers} traveler{totalTravelers > 1 ? 's' : ''}</Typography>
                                {keyword && (
                                    <>
                                        <Divider orientation="vertical" flexItem />
                                        <Chip label={keyword} size="small" onDelete={() => setKeyword('')} />
                                    </>
                                )}
                            </Box>
                            <Button variant="contained" sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#009688' } }}>
                                Modify Search
                            </Button>
                        </Paper>
                    </Box>
                </Container>
            </Box>

            {/* Main Content */}
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Grid container spacing={3}>
                    {/* Filter Sidebar */}
                    {!isMobile && (
                        <Grid item md={3}>
                            <FilterSidebar filters={filters} onChange={setFilters} maxPrice={Math.max(...activities.map((a) => a.price), 200)} resultCount={filteredActivities.length} />
                        </Grid>
                    )}

                    {/* Results */}
                    <Grid item xs={12} md={9}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {destination}: {filteredActivities.length} things to do
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isMobile && (
                                    <Button variant="outlined" startIcon={<TuneRounded />} onClick={() => setFilterDrawerOpen(true)} sx={{ borderRadius: 2, textTransform: 'none' }}>
                                        Filters
                                    </Button>
                                )}
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <InputLabel>Sort by</InputLabel>
                                    <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value as SortOption)} sx={{ borderRadius: 2 }}>
                                        {SORT_OPTIONS.map((opt) => (<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        {loading ? (
                            <Box>{[1, 2, 3].map((i) => (<Skeleton key={i} variant="rounded" height={240} sx={{ mb: 2, borderRadius: 2 }} />))}</Box>
                        ) : filteredActivities.length === 0 ? (
                            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                                <Attractions sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>No activities found</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Try adjusting your filters or search criteria</Typography>
                            </Paper>
                        ) : (
                            <Box>{filteredActivities.map((activity) => (<ActivityCard key={activity.id} activity={activity} travelers={totalTravelers} onSelect={handleSelectActivity} />))}</Box>
                        )}
                    </Grid>
                </Grid>
            </Container>

            {/* Mobile Filter Drawer */}
            <Drawer anchor="bottom" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)} PaperProps={{ sx: { maxHeight: '85vh', borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}>
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Filters</Typography>
                        <IconButton onClick={() => setFilterDrawerOpen(false)}><Close /></IconButton>
                    </Box>
                    <FilterSidebar filters={filters} onChange={setFilters} maxPrice={Math.max(...activities.map((a) => a.price), 200)} resultCount={filteredActivities.length} />
                    <Button fullWidth variant="contained" onClick={() => setFilterDrawerOpen(false)} sx={{ mt: 2, bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#009688' } }}>
                        Show {filteredActivities.length} Results
                    </Button>
                </Box>
            </Drawer>
        </Box>
    );
};

export default ActivitySearchResultsPage;
